import { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../hooks/useTheme';
import {
  LayoutDashboard, Truck, CalendarDays, AlertTriangle,
  Users, Settings, LogOut, Sun, Moon, Menu, X, ClipboardList, Mail,
} from 'lucide-react';

const NAV = [
  { to: '/dashboard',     icon: LayoutDashboard, label: 'Dashboard',       short: 'Start'                             },
  { to: '/fuhrpark',      icon: Truck,           label: 'Fuhrpark',        short: 'Fuhrpark', perm: 'fuhrpark'        },
  { to: '/kalender',      icon: CalendarDays,    label: 'Kalender',        short: 'Kalender'                          },
  { to: '/schaden',       icon: AlertTriangle,   label: 'Schadensmeldung', short: 'Schäden',  perm: 'schaden'         },
  { to: '/packliste',     icon: ClipboardList,   label: 'Packlisten',      short: 'Packen',   perm: 'packliste'       },
  { to: '/benutzer',      icon: Users,           label: 'Benutzer',        short: 'Nutzer',   perm: 'benutzer_manage' },
  { to: '/einstellungen', icon: Settings,        label: 'Einstellungen',   short: 'Mehr'                              },
  { to: '/email',         icon: Mail,            label: 'E-Mail',          short: 'E-Mail',   perm: 'email_manage'    },
];

const ROLE_LABELS = { admin: 'Administrator', benutzer: 'Benutzer', mitarbeiter: 'Mitarbeiter' };

export default function Layout({ children, title }) {
  const { user, logout, can }        = useAuth();
  const { theme, toggleTheme }       = useTheme();
  const navigate                     = useNavigate();
  const location                     = useLocation();
  const [drawerOpen, setDrawerOpen]  = useState(false);

  // Drawer schließen bei Route-Wechsel
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Body scroll sperren wenn Drawer offen
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  const doLogout = () => { logout(); navigate('/login'); };

  const initials = (user?.name || 'U')
    .split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  const visibleNav  = NAV.filter(n => !n.perm || can(n.perm));
  const bottomItems = visibleNav.slice(0, 5);

  const isActive = (to) =>
    location.pathname === to || location.pathname.startsWith(to + '/');

  // Nav-Link Komponente für Sidebar & Drawer
  const NavItem = ({ item, onClick }) => {
    const Icon   = item.icon;
    const active = isActive(item.to);
    return (
      <NavLink
        to={item.to}
        onClick={onClick}
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '9px 12px', borderRadius: 9,
          color: active ? '#e63946' : 'rgba(255,255,255,.55)',
          background: active ? 'rgba(230,57,70,.18)' : 'transparent',
          fontSize: '.84rem', fontWeight: 600,
          textDecoration: 'none',
          transition: 'background 140ms, color 140ms',
          border: 'none', cursor: 'pointer',
          width: '100%', textAlign: 'left',
          fontFamily: 'inherit',
        }}
        onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,.07)'; }}
        onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}>
        <Icon size={16} strokeWidth={2} style={{ flexShrink: 0 }} />
        {item.label}
      </NavLink>
    );
  };

  const ActionBtn = ({ icon: Icon, label, onClick, danger }) => (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '9px 12px', borderRadius: 9,
        color: danger ? '#fca5a5' : 'rgba(255,255,255,.55)',
        background: 'transparent',
        fontSize: '.84rem', fontWeight: 600,
        border: 'none', cursor: 'pointer',
        width: '100%', textAlign: 'left',
        fontFamily: 'inherit',
        transition: 'background 140ms, color 140ms',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,.07)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
      <Icon size={16} strokeWidth={2} style={{ flexShrink: 0 }} />
      {label}
    </button>
  );

  const UserInfo = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px', borderTop: '1px solid rgba(255,255,255,.07)' }}>
      <div style={{
        width: 32, height: 32, borderRadius: '50%', background: '#e63946',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#fff', fontSize: '.72rem', fontWeight: 800, flexShrink: 0,
      }}>{initials}</div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '.78rem', fontWeight: 700, color: '#f1f5f9', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.name}</div>
        <div style={{ fontSize: '.65rem', color: 'rgba(255,255,255,.4)' }}>{ROLE_LABELS[user?.role] || user?.role}</div>
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', minHeight: '100dvh', background: 'var(--bg)' }}>

      {/* ── Desktop Sidebar (nur ≥ 901px) ── */}
      <aside style={{
        position: 'fixed',
        top: 0, left: 0, bottom: 0,
        width: 230,
        background: '#13172b',
        display: 'flex', flexDirection: 'column',
        zIndex: 100,
        overflowY: 'auto', overflowX: 'hidden',
        borderRight: '1px solid rgba(255,255,255,.06)',
      }}
        className="fg-sidebar">
        {/* Logo */}
        <div style={{ padding: '14px', borderBottom: '1px solid rgba(255,255,255,.07)', flexShrink: 0 }}>
          <img src="/logo.jpg" alt="Gerhards" style={{ width: '100%', height: 72, objectFit: 'cover', borderRadius: 10 }} />
        </div>
        {/* Nav */}
        <nav style={{ flex: 1, padding: '12px 8px', display: 'flex', flexDirection: 'column', gap: 2 }}>
          <div style={{ fontSize: '.6rem', fontWeight: 700, color: 'rgba(255,255,255,.25)', letterSpacing: '.12em', textTransform: 'uppercase', padding: '4px 10px 8px' }}>
            Navigation
          </div>
          {visibleNav.map(item => <NavItem key={item.to} item={item} />)}
        </nav>
        {/* Bottom */}
        <div style={{ padding: '8px', borderTop: '1px solid rgba(255,255,255,.07)', flexShrink: 0 }}>
          <ActionBtn
            icon={theme === 'light' ? Moon : Sun}
            label={theme === 'light' ? 'Dunkelmodus' : 'Hellmodus'}
            onClick={toggleTheme}
          />
          <ActionBtn icon={LogOut} label="Abmelden" onClick={doLogout} danger />
          <UserInfo />
        </div>
      </aside>

      {/* ── Mobile Drawer Overlay ── */}
      {drawerOpen && (
        <div
          onClick={() => setDrawerOpen(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 300,
            background: 'rgba(0,0,0,.55)',
            backdropFilter: 'blur(3px)',
            WebkitBackdropFilter: 'blur(3px)',
          }}
        />
      )}

      {/* ── Mobile Drawer (von LINKS) ── */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: drawerOpen ? 0 : '-280px', // Von links einschieben
        bottom: 0,
        width: 270,
        background: '#13172b',
        zIndex: 400,
        display: 'flex', flexDirection: 'column',
        overflowY: 'auto',
        transition: 'left 260ms cubic-bezier(.4,0,.2,1)',
        boxShadow: drawerOpen ? '4px 0 24px rgba(0,0,0,.4)' : 'none',
      }}>
        {/* Drawer Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 12px',
          borderBottom: '1px solid rgba(255,255,255,.07)',
          flexShrink: 0,
        }}>
          <img src="/logo.jpg" alt="Gerhards" style={{ height: 40, borderRadius: 8, objectFit: 'cover' }} />
          <button
            onClick={() => setDrawerOpen(false)}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 34, height: 34, borderRadius: 8,
              background: 'rgba(255,255,255,.08)', color: 'rgba(255,255,255,.7)',
              border: 'none', cursor: 'pointer',
            }}>
            <X size={18} />
          </button>
        </div>

        {/* Drawer User */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '14px 12px',
          borderBottom: '1px solid rgba(255,255,255,.07)',
          flexShrink: 0,
        }}>
          <div style={{
            width: 36, height: 36, borderRadius: '50%', background: '#e63946',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: '.8rem', fontWeight: 800, flexShrink: 0,
          }}>{initials}</div>
          <div>
            <div style={{ fontSize: '.85rem', fontWeight: 700, color: '#f1f5f9' }}>{user?.name}</div>
            <div style={{ fontSize: '.7rem', color: '#e63946', fontWeight: 600, marginTop: 2 }}>
              {ROLE_LABELS[user?.role] || user?.role}
            </div>
          </div>
        </div>

        {/* Drawer Nav */}
        <nav style={{ flex: 1, padding: '12px 8px', display: 'flex', flexDirection: 'column', gap: 2 }}>
          <div style={{ fontSize: '.6rem', fontWeight: 700, color: 'rgba(255,255,255,.25)', letterSpacing: '.12em', textTransform: 'uppercase', padding: '4px 10px 8px' }}>
            Navigation
          </div>
          {visibleNav.map(item => (
            <NavItem key={item.to} item={item} onClick={() => setDrawerOpen(false)} />
          ))}
        </nav>

        {/* Drawer Bottom */}
        <div style={{ padding: '8px', borderTop: '1px solid rgba(255,255,255,.07)', flexShrink: 0 }}>
          <ActionBtn
            icon={theme === 'light' ? Moon : Sun}
            label={theme === 'light' ? 'Dunkelmodus' : 'Hellmodus'}
            onClick={() => { toggleTheme(); setDrawerOpen(false); }}
          />
          <ActionBtn icon={LogOut} label="Abmelden" onClick={() => { doLogout(); setDrawerOpen(false); }} danger />
        </div>
      </div>

      {/* ── Main Content ── */}
      <div style={{
        flex: 1,
        display: 'flex', flexDirection: 'column',
        minHeight: '100dvh', minWidth: 0,
        marginLeft: 0, // Mobile: kein Margin
      }}
        className="fg-main">

        {/* Topbar */}
        <header style={{
          position: 'sticky', top: 0, zIndex: 50,
          background: 'var(--bg-card)',
          borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 16px',
          // iOS Safe Area
          paddingTop: 'env(safe-area-inset-top, 0px)',
          minHeight: 'calc(54px + env(safe-area-inset-top, 0px))',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {/* Hamburger — nur auf Mobile */}
            <button
              onClick={() => setDrawerOpen(true)}
              className="fg-hamburger"
              style={{
                display: 'none', // wird per CSS auf Mobile eingeblendet
                alignItems: 'center', justifyContent: 'center',
                padding: 8, border: 'none', background: 'none',
                cursor: 'pointer', color: 'var(--text)', borderRadius: 8,
              }}>
              <Menu size={22} />
            </button>
            {/* Logo — nur auf Mobile */}
            <img
              src="/logo.jpg" alt=""
              className="fg-topbar-logo"
              style={{
                display: 'none', // wird per CSS auf Mobile eingeblendet
                height: 30, borderRadius: 6, objectFit: 'cover',
              }}
            />
            <span style={{ fontSize: '1rem', fontWeight: 800, color: 'var(--text)' }}>{title}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button
              onClick={toggleTheme}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 8, border: 'none', background: 'none',
                cursor: 'pointer', color: 'var(--text)', borderRadius: 8,
              }}>
              {theme === 'light' ? <Moon size={17} /> : <Sun size={17} />}
            </button>
          </div>
        </header>

        {/* Page Content */}
        <main style={{
          flex: 1,
          padding: '20px 16px',
          paddingBottom: 'calc(72px + env(safe-area-inset-bottom, 0px) + 16px)',
          width: '100%',
          maxWidth: '100%',
        }}
          className="fg-page">
          {children}
        </main>
      </div>

      {/* ── Bottom Navigation (nur Mobile) ── */}
      <nav
        className="fg-bottom-nav"
        style={{
          display: 'none', // wird per CSS auf Mobile eingeblendet
          position: 'fixed', bottom: 0, left: 0, right: 0,
          background: 'var(--bg-card)',
          borderTop: '1px solid var(--border)',
          zIndex: 100,
          boxShadow: '0 -2px 20px rgba(0,0,0,.08)',
        }}>
        <div style={{
          display: 'flex',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}>
          {bottomItems.map(({ to, icon: Icon, short }) => {
            const active = isActive(to);
            return (
              <NavLink
                key={to}
                to={to}
                style={{
                  flex: 1,
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center',
                  padding: '9px 4px 7px', gap: 3,
                  textDecoration: 'none',
                  color: active ? 'var(--accent)' : 'var(--text-faint)',
                  fontSize: '.6rem', fontWeight: 700,
                  position: 'relative',
                  transition: 'color 150ms',
                }}>
                {active && (
                  <div style={{
                    position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
                    width: 28, height: 2, background: 'var(--accent)', borderRadius: '0 0 2px 2px',
                  }} />
                )}
                <Icon size={21} strokeWidth={active ? 2.5 : 2} />
                <span>{short}</span>
              </NavLink>
            );
          })}
          {/* Mehr-Button für restliche Nav-Items */}
          <button
            onClick={() => setDrawerOpen(true)}
            style={{
              flex: 1, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              padding: '9px 4px 7px', gap: 3,
              color: 'var(--text-faint)', fontSize: '.6rem', fontWeight: 700,
              border: 'none', background: 'none', cursor: 'pointer',
              fontFamily: 'inherit',
            }}>
            <Menu size={21} strokeWidth={2} />
            <span>Mehr</span>
          </button>
        </div>
      </nav>

      {/* Responsive CSS */}
      <style>{`
        @media (min-width: 901px) {
          .fg-main { margin-left: 230px !important; }
          .fg-bottom-nav { display: none !important; }
          .fg-page { padding: 24px 28px !important; padding-bottom: 24px !important; max-width: 1100px !important; }
        }
        @media (max-width: 900px) {
          .fg-sidebar { display: none !important; }
          .fg-hamburger { display: flex !important; }
          .fg-topbar-logo { display: block !important; }
          .fg-bottom-nav { display: block !important; }
        }
      `}</style>
    </div>
  );
}
